import { useCallback, useState } from 'react';

/**
 * La bascule automatique / humain — #31 §4.
 *
 * **Une seule bascule, pas quinze.** La mesure désigne l'**origine**, pas le type,
 * comme ce qui noie la conversation : 6,0 envois automatiques pour 2,1 messages
 * humains, et l'automatisme traverse tous les canaux. Quinze bascules au pouce
 * résoudraient un problème que personne n'a — #17 avait déjà refusé la barre
 * d'onglets faute de place, #16 a retenu une liste dépouillée.
 *
 * **Trois règles, non négociables :**
 *
 * 1. **Défaut : tout affiché.** Personne ne découvre l'app avec des messages déjà
 *    cachés — et 13 % des conversations n'ont aucun automatisme, pour lesquelles
 *    tout mécanisme de tri est du poids mort.
 * 2. **Elle survit.** Le filtre web retombe à chaque conversation ouverte
 *    (`useState` dans `ChatThread.tsx:200`, jamais persisté) : #31 a tranché que
 *    c'est « une gêne, pas un modèle ». Au pouce on enchaîne la même tâche.
 * 3. **Un témoin permanent** accompagne l'état masqué — il vit dans l'écran, pas
 *    ici, mais c'est la condition qui rend ce hook acceptable : sans lui on
 *    recrée le danger que #31 visait, répondre sans savoir ce que la cliente a reçu.
 */

const CLE = 'tf_mobile_masquer_automatismes';

function lire(): boolean {
    try {
        return localStorage.getItem(CLE) === '1';
    } catch {
        // Navigation privée, stockage bloqué : on retombe sur le défaut, qui est
        // le comportement sûr — tout afficher.
        return false;
    }
}

export function useMasquageAutomatismes(): [boolean, (valeur: boolean) => void] {
    const [masque, setMasque] = useState<boolean>(lire);

    const changer = useCallback((valeur: boolean) => {
        setMasque(valeur);
        try {
            if (valeur) localStorage.setItem(CLE, '1');
            else localStorage.removeItem(CLE);
        } catch {
            // L'état de la session reste correct même si rien n'est écrit : la
            // bascule fonctionne, elle ne survit simplement pas au redémarrage.
        }
    }, []);

    return [masque, changer];
}
